import { useState, useCallback, useRef, useEffect } from 'react';

const MAX_HISTORY_SIZE = 50;

export const useEditorHistory = (initialValue: string) => {
	const [history, setHistory] = useState<string[]>([initialValue]);
	const [historyIndex, setHistoryIndex] = useState(0);
	const isUndoRedoRef = useRef(false);
	const previousValueRef = useRef(initialValue);

	useEffect(() => {
		if (
			initialValue !== previousValueRef.current &&
			initialValue !== history[historyIndex] &&
			!isUndoRedoRef.current
		) {
			previousValueRef.current = initialValue;
			setHistory([initialValue]);
			setHistoryIndex(0);
		}
		isUndoRedoRef.current = false;
	}, [initialValue, history, historyIndex]);

	const saveToHistory = useCallback(
		(newValue: string) => {
			if (newValue === history[historyIndex]) {
				return;
			}

			setHistory((prev) => {
				const newHistory = prev.slice(0, historyIndex + 1);
				newHistory.push(newValue);

				if (newHistory.length > MAX_HISTORY_SIZE) {
					newHistory.shift();
					return newHistory;
				}

				return newHistory;
			});

			setHistoryIndex((prev) => {
				const newIndex = Math.min(prev + 1, MAX_HISTORY_SIZE - 1);
				previousValueRef.current = newValue;
				return newIndex;
			});
		},
		[historyIndex, history]
	);

	const undo = useCallback(() => {
		if (historyIndex > 0) {
			isUndoRedoRef.current = true;
			const newIndex = historyIndex - 1;
			setHistoryIndex(newIndex);
			previousValueRef.current = history[newIndex];
			return history[newIndex];
		}
		return null;
	}, [historyIndex, history]);

	const redo = useCallback(() => {
		if (historyIndex < history.length - 1) {
			isUndoRedoRef.current = true;
			const newIndex = historyIndex + 1;
			setHistoryIndex(newIndex);
			previousValueRef.current = history[newIndex];
			return history[newIndex];
		}
		return null;
	}, [historyIndex, history]);

	const canUndo = historyIndex > 0;
	const canRedo = historyIndex < history.length - 1;

	return {
		saveToHistory,
		undo,
		redo,
		canUndo,
		canRedo,
	};
};
